import {
  Alert,
  AlertSection,
  AlertType,
  AloftDate,
  AloftPrice,
  CompType,
  DismissedAlert,
  demoteAlertSeverity,
  dismissedAlertMatches,
  getDismissedAlertType,
  isUndefined,
  schAloftArea,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import {
  type MutableRefObject,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  getVacantLandChartData,
  getVacantLandSalesExponentialFit,
} from '../../../../components/graphs/costApproach/utils.ts';
import { useVacantLandSales } from '../../../../hooks/useVacantLandSales.tsx';
import { selectCompAdjustments } from '../../../../redux/features/selectors/adjustmentSelectors.ts';
import { selectAlerts } from '../../../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import {
  selectCommentary,
  selectCommentaryV2,
} from '../../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOpinionOfSiteValue } from '../../../../redux/features/selectors/opinionOfSiteValueSelectors.ts';
import {
  selectCompSequence,
  selectCompTypes,
  selectEffectiveDate,
  selectIncludeCostApproach,
  selectIsMockOrder,
  selectOrder,
  selectSalesComparisonApproach,
} from '../../../../redux/features/selectors/orderSelectors.ts';
import { selectPriorSales } from '../../../../redux/features/selectors/priorSalesSelectors.ts';
import { selectEditablePropertyDataById } from '../../../../redux/features/selectors/propertyDataSelectors/editablePropertyDataSelectors.ts';
import {
  selectImprovementEntityIds,
  selectImprovementsEntities,
} from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../../../../redux/features/selectors/propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyDataEntities } from '../../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { selectSubjectUnitRates } from '../../../../redux/features/selectors/propertyDataSelectors/unitRateSelectors.ts';
import { selectImprovementsCosts } from '../../../../redux/features/selectors/selectImprovementsCost.ts';
import { selectReconciliation } from '../../../../redux/features/selectors/selectReconciliation.ts';
import { selectAllCompTypesReconciliationData } from '../../../../redux/features/selectors/selectReconciliationData.ts';
import { selectSiteCostPerSf } from '../../../../redux/features/selectors/selectSiteCostPerSf.ts';
import { selectSiteValues } from '../../../../redux/features/selectors/selectSiteValue.ts';
import {
  selectFeatureGridActiveConflictedFields,
  selectFeatureGridConflictedFields,
  selectTriggeredAlerts,
} from '../../../../redux/features/selectors/uiSelectors.ts';
import {
  clearFocusedCostApproachPropertyId,
  clearTriggeredAlert,
  unsuppressAlertToast,
} from '../../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { usePropertyImageEntities } from '../../../../utils/usePropertyImages.tsx';
import { allCompsAdjustedSame } from '../../../alerts/definitions/adjustments/allCompsAdjustedSame.ts';
import { inconsistentAdjustmentRate } from '../../../alerts/definitions/adjustments/inconsistentAdjustmentRate.ts';
import { inconsistentCompAdjustment } from '../../../alerts/definitions/adjustments/inconsistentCompAdjustment.ts';
import { limitedAdjustedSalePriceRange } from '../../../alerts/definitions/adjustments/limitedAdjustedSalePriceRange.ts';
import { missingCompAdjustment } from '../../../alerts/definitions/adjustments/missingCompAdjustment.ts';
import { overlyAdjustedComparable } from '../../../alerts/definitions/adjustments/overlyAdjustedComparable.ts';
import { unexpectedCompAdjustment } from '../../../alerts/definitions/adjustments/unexpectedCompAdjustment.ts';
import { aivAboveC3 } from '../../../alerts/definitions/comparable/aivAboveC3.ts';
import { arvBelowC4 } from '../../../alerts/definitions/comparable/arvBelowC4.ts';
import { inconsistentConditionRating } from '../../../alerts/definitions/comparable/inconsistentConditionRating.ts';
import { arvConditionBelowAiv } from '../../../alerts/definitions/comparable/arvConditionBelowAiv.ts';
import { bracketedSubject } from '../../../alerts/definitions/comparable/bracketedSubject.ts';
import { compOlderThan12Months } from '../../../alerts/definitions/comparable/compOlderThan12Months.ts';
import { compSaleDateToSubject } from '../../../alerts/definitions/comparable/compSaleDateToSubject.ts';
import { inconsistentFeature } from '../../../alerts/definitions/comparable/inconsistentFeature.ts';
import { inconsistentSubjectBedBathCount } from '../../../alerts/definitions/comparable/inconsistentSubjectBedBathCount.ts';
import { inconsistentSubjectGLA } from '../../../alerts/definitions/comparable/inconsistentSubjectGLA.ts';
import { inconsistentSubjectSiteSize } from '../../../alerts/definitions/comparable/inconsistentSubjectSiteSize.ts';
import { insufficientProximateComps } from '../../../alerts/definitions/comparable/insufficientProximateComps.ts';
import { insufficientRecentComp } from '../../../alerts/definitions/comparable/insufficientRecentComp.ts';
import { lessThanThreeSettledSales } from '../../../alerts/definitions/comparable/lessThanThreeSettledSales.ts';
import { missingPropertyData } from '../../../alerts/definitions/comparable/missingPropertyData.ts';
import { outsideProximityRequirement } from '../../../alerts/definitions/comparable/outsideProximityRequirement.ts';
import { propertyWithoutPhoto } from '../../../alerts/definitions/comparable/propertyWithoutPhoto.ts';
import { similarComparableToSubjectPriorSale } from '../../../alerts/definitions/comparable/similarComparableToSubjectPriorSale.ts';
import { effectiveAgeLowerThanActualAge } from '../../../alerts/definitions/depreciatedCost/effectiveAgeLowerThanActualAge.ts';
import { inconsistentSiteSizeToSiteValue } from '../../../alerts/definitions/depreciatedCost/inconsistentSiteSizeToSiteValue.ts';
import { negativeSiteValue } from '../../../alerts/definitions/depreciatedCost/negativeSiteValue.ts';
import { incompleteRequiredFields } from '../../../alerts/definitions/requiredFields/incompleteRequiredFields.ts';
import { AlertDefinition, AlertDefinitionParams } from '../../../alerts/types.ts';
import { useDesktopReconciliationComparison } from '../../../reconciliation/hooks/useDesktopReconciliationComparison.ts';
import { AlertsContext, AlertsRefContext, EMPTY_ALERTS } from './alertsContext.ts';

const DISMISSED_ALERT_ALIASES: Partial<Record<AlertType, AlertType>> = {
  inconsistentCompGlaAdjustment: 'inconsistentCompAdjustment',
  inconsistentCompSiteAdjustment: 'inconsistentCompAdjustment',
  smallestSiteHasLowestValue: 'inconsistentSiteSizeToSiteValue',
  largestSiteHasHighestValue: 'inconsistentSiteSizeToSiteValue',
  conditionAdjustedUpAgainstSubject: 'inconsistentCompAdjustment',
  conditionAdjustedUpAgainstSubjectAiv: 'inconsistentCompAdjustmentAiv',
  conditionAdjustedUpAgainstSubjectArv: 'inconsistentCompAdjustmentArv',
};

const isAlertDismissed = (alert: Alert, dismissedAlerts: DismissedAlert[]): boolean => {
  if (dismissedAlerts.some((entry) => dismissedAlertMatches(entry, alert))) return true;

  return dismissedAlerts.some((entry) => {
    const dismissedType = getDismissedAlertType(entry);
    return DISMISSED_ALERT_ALIASES[dismissedType] === alert.type;
  });
};

const buildAlertSection = (
  alertDefinitions: AlertDefinition[],
  params: AlertDefinitionParams,
  dismissedAlerts: DismissedAlert[],
  isInspectifyOrder: boolean,
  isMockOrder: boolean,
  isInternalValuation: boolean,
): Alert[] =>
  alertDefinitions
    .flatMap((alertDefinition) => alertDefinition(params))
    .filter(
      (alert) =>
        (!isInspectifyOrder && alert.orderType.includes('saas')) ||
        (isInspectifyOrder && alert.orderType.includes('appraisify')),
    )
    .map((alert) =>
      demoteAlertSeverity(
        {
          ...alert,
          status: isAlertDismissed(alert, dismissedAlerts) ? 'dismissed' : alert.status,
        },
        { isMockOrder, isInspectifyOrder, isInternalValuation },
      ),
    );

const splitCompSequenceByType = (
  compSequence: string[],
  compTypes: { [key: string]: CompType | undefined } | undefined,
) => ({
  aiv: compSequence.filter((id) => !compTypes?.[id] || compTypes?.[id] === 'aiv'),
  arv: compSequence.filter((id) => compTypes?.[id] === 'arv'),
});

export const AlertsProvider = ({ children }: { children: ReactNode }) => {
  const alertsRef = useRef(EMPTY_ALERTS);
  const alerts = useComputeAlerts();
  alertsRef.current = alerts;

  return (
    <AlertsRefContext.Provider value={alertsRef}>
      <AlertsContext.Provider value={alerts}>{children}</AlertsContext.Provider>
    </AlertsRefContext.Provider>
  );
};

export const useAlerts = (): Record<AlertSection, Alert[]> => {
  const alerts = useContext(AlertsContext);
  if (!alerts) {
    throw new Error('useAlerts must be used within an AlertsProvider');
  }
  return alerts;
};

export const useAlertsRef = (): MutableRefObject<Record<AlertSection, Alert[]>> => {
  const alertsRef = useContext(AlertsRefContext);
  if (!alertsRef) {
    throw new Error('useAlertsRef must be used within an AlertsProvider');
  }
  return alertsRef;
};

const useComputeAlerts = (): Record<AlertSection, Alert[]> => {
  const dispatch = useAppDispatch();
  const { dismissedAlerts } = useAppSelector(selectAlerts);
  const compAdjustments = useAppSelector(selectCompAdjustments);
  const compSequence = useAppSelector(selectCompSequence);
  const compTypes = useAppSelector(selectCompTypes);
  const improvementEntityIds = useAppSelector(selectImprovementEntityIds);
  const improvementCosts = useAppSelector(selectImprovementsCosts);
  const order = useAppSelector(selectOrder);
  const priorSales = useAppSelector(selectPriorSales);
  const propertyDataEntities = useAppSelector(selectPropertyDataEntities);
  const manualAdjustments = useAppSelector(selectManualAdjustmentsEntities);
  const subjectUnitRates = useAppSelector(selectSubjectUnitRates);
  const salesComparisonApproach = useAppSelector(selectSalesComparisonApproach);
  const siteValues = useAppSelector(selectSiteValues);
  const reconciliation = useAppSelector(selectReconciliation);
  const reconciliationData = useAppSelector(selectAllCompTypesReconciliationData);
  const { marketConditionsV2, priorSales: priorSalesCommentary } =
    useAppSelector(selectCommentaryV2);
  const { reconciliation: reconciliationCommentary } = useAppSelector(selectCommentary);
  const isMockOrder = useAppSelector(selectIsMockOrder);
  const { hasAivDiscrepancy, hasArvDiscrepancy } = useDesktopReconciliationComparison({
    opinionOfMarketValue: schAloftPrice.optional().parse(reconciliation.opinionOfMarketValue),
    opinionOfAfterRepairValue: schAloftPrice
      .optional()
      .parse(reconciliation.opinionOfAfterRepairValue),
  });
  const propertyImageEntities = usePropertyImageEntities();
  const activeConflictedFields = useAppSelector(selectFeatureGridActiveConflictedFields);
  const conflictedFields = useAppSelector(selectFeatureGridConflictedFields);
  const effectiveDate = useAppSelector(selectEffectiveDate);

  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const improvements = useAppSelector(selectImprovementsEntities);
  const opinionOfSiteValue = useAppSelector(selectOpinionOfSiteValue);
  const vacantLandSales = useVacantLandSales();
  const subjectEditableData = useAppSelector((state) =>
    selectEditablePropertyDataById(state, 'subject'),
  );
  const subjectCostPerSF = useAppSelector((state) =>
    selectSiteCostPerSf(state, 'subject'),
  )?.toDollarLike();
  const triggeredAlerts = useAppSelector(selectTriggeredAlerts);
  const areAlertsReady = ![
    dismissedAlerts,
    compAdjustments,
    compSequence,
    improvementCosts,
    order,
    priorSales,
    propertyDataEntities,
    siteValues,
    propertyImageEntities,
  ].some(isUndefined);

  const vacantLandValue = useMemo(() => {
    const subjectSiteArea = subjectEditableData?.site?.area;
    const vacantLandSalesChartData = getVacantLandChartData(vacantLandSales);
    if (
      !vacantLandSales ||
      !subjectSiteArea ||
      isUndefined(subjectCostPerSF) ||
      !vacantLandSalesChartData ||
      vacantLandSalesChartData.length < 2
    ) {
      return undefined;
    }

    const siteSize = schAloftArea.parse(subjectSiteArea).getUnitAmount('sqft');

    const { getCostPerSFFromSiteSize } = getVacantLandSalesExponentialFit(vacantLandSalesChartData);

    return AloftPrice.fromDollarLike(
      Math.min(getCostPerSFFromSiteSize(siteSize) * siteSize, Number.MAX_VALUE / 100),
    );
  }, [vacantLandSales, subjectEditableData?.site?.area, subjectCostPerSF]);

  const alertDefinitionParams: AlertDefinitionParams | undefined = useMemo(() => {
    if (!areAlertsReady) return undefined;

    return {
      compAdjustments,
      compSequence,
      improvementCosts,
      order,
      priorSales,
      propertyDataEntities,
      siteValues,
      improvementEntityIds,
      reconciliation,
      propertyImageEntities,
      vacantLandValue,
      activeConflictedFields,
      conflictedFields,
      fullCompSequence: compSequence,
      marketConditionsV2,
      priorSalesCommentary,
      reconciliationCommentary,
      hasAivDiscrepancy,
      hasArvDiscrepancy,
      isMockOrder,
      improvements,
      includeCostApproach,
      opinionOfSiteValue,
      effectiveDate: effectiveDate ? AloftDate.schema.parse(effectiveDate) : AloftDate.today(),
      reconciliationData,
      manualAdjustments,
      subjectUnitRates,
      salesComparisonApproach,
    };
  }, [
    areAlertsReady,
    compAdjustments,
    compSequence,
    improvementCosts,
    order,
    priorSales,
    propertyDataEntities,
    siteValues,
    improvementEntityIds,
    reconciliation,
    propertyImageEntities,
    vacantLandValue,
    activeConflictedFields,
    conflictedFields,
    marketConditionsV2,
    priorSalesCommentary,
    reconciliationCommentary,
    hasAivDiscrepancy,
    hasArvDiscrepancy,
    isMockOrder,
    improvements,
    includeCostApproach,
    opinionOfSiteValue,
    effectiveDate,
    reconciliationData,
    manualAdjustments,
    subjectUnitRates,
    salesComparisonApproach,
  ]);

  const adjustments: Alert[] = useMemo(() => {
    if (!alertDefinitionParams) return [];

    const definitions = [
      limitedAdjustedSalePriceRange,
      allCompsAdjustedSame,
      inconsistentCompAdjustment,
      inconsistentAdjustmentRate,
      missingCompAdjustment,
      unexpectedCompAdjustment,
      overlyAdjustedComparable,
    ];

    if (!order.hasArvOption) {
      return buildAlertSection(
        definitions,
        alertDefinitionParams,
        dismissedAlerts,
        order.isInspectifyOrder,
        isMockOrder,
        order.isInternalValuation,
      );
    }

    const { aiv: aivCompSequence, arv: arvCompSequence } = splitCompSequenceByType(
      compSequence,
      compTypes,
    );

    const aivAlerts = buildAlertSection(
      definitions,
      { ...alertDefinitionParams, compSequence: aivCompSequence, compType: 'aiv' },
      dismissedAlerts,
      order.isInspectifyOrder,
      isMockOrder,
      order.isInternalValuation,
    );
    const arvAlerts = buildAlertSection(
      definitions,
      { ...alertDefinitionParams, compSequence: arvCompSequence, compType: 'arv' },
      dismissedAlerts,
      order.isInspectifyOrder,
      isMockOrder,
      order.isInternalValuation,
    );

    return [...aivAlerts, ...arvAlerts];
  }, [
    alertDefinitionParams,
    dismissedAlerts,
    order.hasArvOption,
    order.isInternalValuation,
    compSequence,
    compTypes,
    isMockOrder,
  ]);

  const comparable: Alert[] = useMemo(() => {
    if (!alertDefinitionParams) return [];

    const orderScopedDefinitions = [incompleteRequiredFields];
    const compDefinitions = [
      lessThanThreeSettledSales,
      insufficientProximateComps,
      insufficientRecentComp,
      bracketedSubject,
      similarComparableToSubjectPriorSale,
      propertyWithoutPhoto,
      missingPropertyData,
      compSaleDateToSubject,
      compOlderThan12Months,
      aivAboveC3,
      arvBelowC4,
      inconsistentConditionRating,
      inconsistentFeature,
    ];

    const subjectDefinitions = [
      arvConditionBelowAiv,
      inconsistentSubjectBedBathCount,
      inconsistentSubjectGLA,
      inconsistentSubjectSiteSize,
      inconsistentFeature,
    ];

    const orderScopedAlerts = buildAlertSection(
      orderScopedDefinitions,
      alertDefinitionParams,
      dismissedAlerts,
      order.isInspectifyOrder,
      isMockOrder,
      order.isInternalValuation,
    );

    const proximityAlerts = buildAlertSection(
      [outsideProximityRequirement],
      alertDefinitionParams,
      dismissedAlerts,
      order.isInspectifyOrder,
      isMockOrder,
      order.isInternalValuation,
    );

    if (!order.hasArvOption) {
      return [
        ...buildAlertSection(
          [...orderScopedDefinitions, ...compDefinitions, ...subjectDefinitions],
          alertDefinitionParams,
          dismissedAlerts,
          order.isInspectifyOrder,
          isMockOrder,
          order.isInternalValuation,
        ),
        ...proximityAlerts,
      ];
    }

    const { aiv: aivCompSequence, arv: arvCompSequence } = splitCompSequenceByType(
      compSequence,
      compTypes,
    );

    const aivAlerts = buildAlertSection(
      compDefinitions,
      {
        ...alertDefinitionParams,
        compSequence: aivCompSequence,
        compType: 'aiv',
        propertyType: 'comp',
      },
      dismissedAlerts,
      order.isInspectifyOrder,
      isMockOrder,
      order.isInternalValuation,
    );
    const arvAlerts = buildAlertSection(
      compDefinitions,
      {
        ...alertDefinitionParams,
        compSequence: arvCompSequence,
        compType: 'arv',
        propertyType: 'comp',
      },
      dismissedAlerts,
      order.isInspectifyOrder,
      isMockOrder,
      order.isInternalValuation,
    );

    const subjectAlerts = buildAlertSection(
      subjectDefinitions,
      { ...alertDefinitionParams, propertyType: 'subject' },
      dismissedAlerts,
      order.isInspectifyOrder,
      isMockOrder,
      order.isInternalValuation,
    );

    return [...orderScopedAlerts, ...subjectAlerts, ...aivAlerts, ...arvAlerts, ...proximityAlerts];
  }, [
    alertDefinitionParams,
    dismissedAlerts,
    order.hasArvOption,
    order.isInternalValuation,
    compSequence,
    compTypes,
    propertyImageEntities,
    isMockOrder,
  ]);

  const depreciatedCost: Alert[] = useMemo(() => {
    if (!alertDefinitionParams) return [];

    return buildAlertSection(
      [
        ...(includeCostApproach
          ? [
              effectiveAgeLowerThanActualAge,
              negativeSiteValue,
              // siteValueIsLessThanVacantLandValue, // AV-940
              inconsistentSiteSizeToSiteValue,
              // bracketedSiteToValueRatio, // AV-940
              // limitedSiteToValueRatioRange, // AV-940
            ]
          : []),
      ],
      alertDefinitionParams,
      dismissedAlerts,
      order.isInspectifyOrder,
      isMockOrder,
      order.isInternalValuation,
    );
  }, [
    alertDefinitionParams,
    dismissedAlerts,
    includeCostApproach,
    isMockOrder,
    order.isInternalValuation,
  ]);

  useEffect(() => {
    if (!areAlertsReady) {
      return;
    }

    const activeAlertTypes = new Set(
      [...comparable, ...depreciatedCost, ...adjustments].map((alert) => alert.type),
    );
    const staleTypes = triggeredAlerts.filter((type) => !activeAlertTypes.has(type));
    staleTypes.forEach((type) => {
      dispatch(clearTriggeredAlert(type));
      dispatch(unsuppressAlertToast(type));
      if (type === 'negativeSiteValue') {
        dispatch(clearFocusedCostApproachPropertyId());
      }
    });
  }, [adjustments, areAlertsReady, comparable, depreciatedCost, dispatch, triggeredAlerts]);

  return useMemo(
    () => (areAlertsReady ? { comparable, depreciatedCost, adjustments } : EMPTY_ALERTS),
    [adjustments, areAlertsReady, comparable, depreciatedCost],
  );
};
